/**
 * VaultFilePickerDialog — shared picker for selecting Vault Documents.
 *
 * Replaces the legacy `@/components/files/FileBrowser` + `FileUpload`
 * pickers (which talked to `user_files`). One dialog, used by every
 * arche-specific "pick a file" surface (chat AI-context, Travel
 * attachments, Social Media media picker, Image Editor file picker,
 * Icon dev-console picker, image-generator reference image).
 *
 * Surfaces a list of Vault documents from the user's Personal Holder,
 * optionally filtered by MIME family; supports upload-new via the
 * Vault upload-init / presigned PUT / finalize flow. Returns a
 * `VaultPickerResult` with both the durable `documentId` (re-resolvable
 * later) and a fresh 1-hour `downloadUrl` (for immediate display).
 *
 * Single-select is the default; pass `multiSelect` for galleries.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  File as FileIcon,
  Image as ImageIcon,
  Loader2,
  Music,
  Search,
  Upload,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { api, type VaultDocumentDto } from '@/lib/api';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface VaultPickerResult {
  /** Vault document id — the durable handle. */
  documentId: string;
  fileName: string;
  mimeType: string | null;
  sizeBytes: number;
  /** Vault s3Key — usable as a stable ref for downstream API calls. */
  s3Key: string;
  /** Fresh 1-hour signed download URL for `<img src>`, `<audio src>`, fetch(). */
  downloadUrl: string;
}

/** Built-in MIME family filters. */
export type VaultPickerMimeFamily = 'image' | 'audio' | 'video' | 'document';

export interface VaultFilePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  /** Single-select callback. Fires when the user confirms a selection. */
  onFileSelected?: (file: VaultPickerResult) => void;
  /** Multi-select callback. Required when `multiSelect` is true. */
  onFilesSelected?: (files: VaultPickerResult[]) => void;

  /** Default: false. When true, the dialog renders a checkbox column. */
  multiSelect?: boolean;

  /** Dialog title — defaults to "Select File". */
  title?: string;
  /** Short description shown below the title. */
  description?: string;
  /** Confirm-button label. Defaults to "Select" / "Add N files". */
  confirmLabel?: string;

  /** Filter listed docs by a MIME family or a custom predicate. */
  mimeFilter?: VaultPickerMimeFamily | ((doc: VaultDocumentDto) => boolean);

  /** Show / hide the "Upload new" button. Default: true. */
  allowUpload?: boolean;
  /** Restrict the upload `<input>`'s accept attribute. */
  uploadAccept?: string;
  /** Per-file cap on uploads (bytes). Default: 50 MB. */
  maxUploadSize?: number;
  /** Optional pre-upload validator. Return a string to reject, null to allow. */
  validateUpload?: (file: File) => string | null;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const DEFAULT_MAX_UPLOAD = 50 * 1024 * 1024;

function mimeFamilyOf(mime: string | null | undefined): VaultPickerMimeFamily | 'other' {
  if (!mime) return 'other';
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (
    mime.startsWith('text/') ||
    mime === 'application/pdf' ||
    mime === 'application/json' ||
    mime.includes('officedocument') ||
    mime === 'application/msword'
  ) {
    return 'document';
  }
  return 'other';
}

function iconFor(mime: string | null | undefined) {
  switch (mimeFamilyOf(mime)) {
    case 'image':
      return ImageIcon;
    case 'audio':
      return Music;
    default:
      return FileIcon;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function passesFilter(
  doc: VaultDocumentDto,
  filter: VaultFilePickerDialogProps['mimeFilter'],
): boolean {
  if (!filter) return true;
  if (typeof filter === 'function') return filter(doc);
  return mimeFamilyOf(doc.mimeType) === filter;
}

async function sha256HexOf(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function toPickerResult(doc: VaultDocumentDto): Promise<VaultPickerResult> {
  const { url } = await api.getVaultDocumentDownloadUrl(doc.documentId);
  return {
    documentId: doc.documentId,
    fileName: doc.name,
    mimeType: doc.mimeType,
    sizeBytes: doc.sizeBytes,
    s3Key: doc.s3Key,
    downloadUrl: url,
  };
}

// ---------------------------------------------------------------------------
// Panel — non-dialog version. Use directly when embedding the picker
// inside a multi-tab modal (see social-media MediaPickerModal).
// ---------------------------------------------------------------------------

export interface VaultFilePickerPanelProps {
  isActive: boolean;
  onFileSelected?: (file: VaultPickerResult) => void;
  onFilesSelected?: (files: VaultPickerResult[]) => void;
  multiSelect?: boolean;
  confirmLabel?: string;
  mimeFilter?: VaultFilePickerDialogProps['mimeFilter'];
  allowUpload?: boolean;
  uploadAccept?: string;
  maxUploadSize?: number;
  validateUpload?: VaultFilePickerDialogProps['validateUpload'];
  /** Optional cap on how many entries can be in the selection set. */
  selectionLimit?: number;
  /** Optional className for the outer container. */
  className?: string;
}

export function VaultFilePickerPanel({
  isActive,
  onFileSelected,
  onFilesSelected,
  multiSelect = false,
  confirmLabel,
  mimeFilter,
  allowUpload = true,
  uploadAccept,
  maxUploadSize = DEFAULT_MAX_UPLOAD,
  validateUpload,
  selectionLimit,
  className,
}: VaultFilePickerPanelProps) {
  const [docs, setDocs] = useState<VaultDocumentDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Map<string, VaultDocumentDto>>(new Map());
  const [holderId, setHolderId] = useState<string | null>(null);
  /** documentId → 1-hour signed URL for image thumbnails. Populated
   *  lazily after the list loads (one presign per image-mime row)
   *  rather than eagerly during list response — the list endpoint
   *  doesn't carry signed URLs and minting them on the read path
   *  would amplify cost. Pending rows fall back to the generic icon
   *  until their URL resolves. */
  const [thumbnails, setThumbnails] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!isActive) return;
    setSelected(new Map());
    setSearch('');
    setThumbnails(new Map());
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const memberships = await api.listMyVaultMemberships();
        if (cancelled) return;
        if (!memberships.personalHolder) {
          toast({
            title: 'Workspace not provisioned',
            description: 'Your personal workspace is missing.',
            variant: 'destructive',
          });
          return;
        }
        setHolderId(memberships.personalHolder.holderId);
        // No `status` filter: the picker wants any document whose
        // bytes are downloadable, which is the union of `uploaded`,
        // `extracting`, `available`, and `extraction_failed`. The
        // single-status server filter can't express that union, and
        // pinning it to `available` silently hid every image / PSD /
        // SVG / video / audio — none of those go through text
        // extraction so they sit at `uploaded` forever. `pending`
        // rows (bytes not yet PUT to S3) are dropped client-side
        // below. Archived + deleted are excluded by the server default.
        const result = await api.listVaultDocuments(memberships.personalHolder.holderId, {
          limit: 500,
        });
        if (cancelled) return;
        setDocs(result.documents);
      } catch (err) {
        if (cancelled) return;
        toast({
          title: 'Failed to load files',
          description: err instanceof Error ? err.message : String(err),
          variant: 'destructive',
        });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [isActive]);

  // Resolve presigned download URLs for image-mime docs so the list
  // can render real thumbnails instead of the generic ImageIcon. Runs
  // after `docs` populates; each URL lands in the map as it returns
  // so the user sees thumbnails fill in progressively rather than
  // waiting for all of them. `Promise.allSettled` keeps a single
  // failed presign from blocking the rest. Skipped when no image
  // docs are present so non-image pickers (Document Sign, etc.)
  // don't trigger any extra requests.
  useEffect(() => {
    if (!isActive || docs.length === 0) return;
    const imageDocs = docs.filter((d) => mimeFamilyOf(d.mimeType) === 'image');
    if (imageDocs.length === 0) return;
    let cancelled = false;
    void Promise.allSettled(
      imageDocs.map(async (doc) => {
        const { url } = await api.getVaultDocumentDownloadUrl(doc.documentId);
        if (cancelled) return;
        setThumbnails((prev) => {
          if (prev.has(doc.documentId)) return prev;
          const next = new Map(prev);
          next.set(doc.documentId, url);
          return next;
        });
      }),
    );
    return () => {
      cancelled = true;
    };
  }, [docs, isActive]);

  const filtered = docs.filter((d) => {
    // `pending` = row created but bytes not yet PUT to S3. Trying to
    // download one would 404, so don't surface them. Every other
    // non-archived/non-deleted status has bytes in the bucket and is
    // a valid selection target.
    if (d.status === 'pending') return false;
    if (!passesFilter(d, mimeFilter)) return false;
    if (!search.trim()) return true;
    return d.name.toLowerCase().includes(search.toLowerCase());
  });

  const toggleSelection = useCallback(
    (doc: VaultDocumentDto) => {
      setSelected((prev) => {
        const next = new Map(prev);
        if (next.has(doc.documentId)) {
          next.delete(doc.documentId);
          return next;
        }
        if (!multiSelect) {
          next.clear();
          next.set(doc.documentId, doc);
          return next;
        }
        if (selectionLimit !== undefined && next.size >= selectionLimit) {
          toast({
            title: 'Selection limit reached',
            description: `You can select at most ${selectionLimit} file${selectionLimit === 1 ? '' : 's'}.`,
            variant: 'destructive',
          });
          return next;
        }
        next.set(doc.documentId, doc);
        return next;
      });
    },
    [multiSelect, selectionLimit],
  );

  const handleUpload = useCallback(
    async (file: File) => {
      if (!holderId) return;
      if (file.size > maxUploadSize) {
        toast({
          title: 'File too large',
          description: `Max ${formatBytes(maxUploadSize)}.`,
          variant: 'destructive',
        });
        return;
      }
      if (validateUpload) {
        const err = validateUpload(file);
        if (err) {
          toast({ title: 'Upload rejected', description: err, variant: 'destructive' });
          return;
        }
      }
      setIsUploading(true);
      try {
        const sha = await sha256HexOf(file);
        const init = await api.initVaultDocumentUpload({
          holderId,
          name: file.name,
          mimeType: file.type || 'application/octet-stream',
          sizeBytes: file.size,
          checksumSha256: sha,
        });
        const put = await fetch(init.uploadUrl, {
          method: 'PUT',
          headers: init.requiredHeaders,
          body: file,
        });
        if (!put.ok) {
          throw new Error(`Upload failed (HTTP ${put.status})`);
        }
        const finalized = await api.finalizeVaultDocumentUpload(init.document.documentId, {
          checksumSha256: sha,
        });
        setDocs((prev) => [finalized.document, ...prev]);
        setSelected((prev) => {
          const next = new Map(prev);
          if (!multiSelect) next.clear();
          next.set(finalized.document.documentId, finalized.document);
          return next;
        });
      } catch (err) {
        toast({
          title: 'Upload failed',
          description: err instanceof Error ? err.message : String(err),
          variant: 'destructive',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [holderId, maxUploadSize, validateUpload, multiSelect],
  );

  const handleConfirm = useCallback(async () => {
    if (selected.size === 0) return;
    setIsConfirming(true);
    try {
      const results = await Promise.all(Array.from(selected.values()).map(toPickerResult));
      if (multiSelect && onFilesSelected) {
        onFilesSelected(results);
      } else if (onFileSelected) {
        onFileSelected(results[0]);
      }
    } catch (err) {
      toast({
        title: 'Selection failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setIsConfirming(false);
    }
  }, [selected, multiSelect, onFilesSelected, onFileSelected]);

  /** Confirm with a single doc directly (double-click flow). Bypasses
   *  `selected` because the click + double-click sequence races the
   *  toggleSelection setState and the doc the user actually double-
   *  clicked is unambiguous from the event. In multi-select mode this
   *  intentionally returns only the double-clicked doc — the gesture
   *  reads as "open this one now," not "add to existing selection." */
  const confirmDoc = useCallback(
    async (doc: VaultDocumentDto) => {
      setIsConfirming(true);
      try {
        const result = await toPickerResult(doc);
        if (multiSelect && onFilesSelected) {
          onFilesSelected([result]);
        } else if (onFileSelected) {
          onFileSelected(result);
        }
      } catch (err) {
        toast({
          title: 'Selection failed',
          description: err instanceof Error ? err.message : String(err),
          variant: 'destructive',
        });
      } finally {
        setIsConfirming(false);
      }
    },
    [multiSelect, onFilesSelected, onFileSelected],
  );

  const computedConfirmLabel =
    confirmLabel ??
    (multiSelect
      ? selected.size > 0
        ? `Add ${selected.size} ${selected.size === 1 ? 'file' : 'files'}`
        : 'Add files'
      : 'Select');

  return (
    <div className={cn('flex flex-col h-full min-h-0', className)}>
      <div className="flex items-center gap-2 mb-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search files..."
            className="pl-8"
          />
        </div>
        {allowUpload && (
          <Button asChild variant="outline" disabled={isUploading || isConfirming}>
            <label className="cursor-pointer">
              {isUploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload
              <input
                type="file"
                accept={uploadAccept}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleUpload(f);
                  e.target.value = '';
                }}
              />
            </label>
          </Button>
        )}
      </div>

      <div className="flex-1 min-h-0 border rounded-lg overflow-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Loading files...
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-sm text-muted-foreground text-center px-6">
            {docs.length === 0
              ? 'No files yet. Upload one to get started.'
              : 'No files match your search or filter.'}
          </div>
        ) : (
          <div className="divide-y">
            {filtered.map((doc) => {
              const Icon = iconFor(doc.mimeType);
              const isSelected = selected.has(doc.documentId);
              const thumbnailUrl = thumbnails.get(doc.documentId);
              return (
                <button
                  key={doc.documentId}
                  type="button"
                  onClick={() => toggleSelection(doc)}
                  /* Double-click = "open this one now". Bypasses the
                     selection state via confirmDoc so the doc the user
                     actually double-clicked is the one delivered to
                     the parent. */
                  onDoubleClick={() => void confirmDoc(doc)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/50 transition-colors',
                    isSelected && 'bg-primary/10 hover:bg-primary/15',
                  )}
                >
                  {/* Unified 36×36 thumbnail/icon slot — image rows
                      show the real bytes (presigned URL), everything
                      else gets the mime-family icon centered. Fixed
                      width keeps rows aligned across mixed types. */}
                  <div className="h-9 w-9 shrink-0 flex items-center justify-center rounded bg-muted/40 overflow-hidden">
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Icon className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-sm font-medium">{doc.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {doc.mimeType || 'unknown'} · {formatBytes(doc.sizeBytes)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 pt-3">
        {selected.size > 0 && (
          <div className="mr-auto text-sm text-muted-foreground flex items-center gap-2">
            <span>{selected.size} selected</span>
            <button
              type="button"
              onClick={() => setSelected(new Map())}
              className="text-xs underline hover:text-foreground"
            >
              <X className="h-3 w-3 inline mr-0.5" /> clear
            </button>
          </div>
        )}
        <Button
          onClick={handleConfirm}
          disabled={selected.size === 0 || isConfirming}
          className="ml-auto"
        >
          {isConfirming ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Loading...
            </>
          ) : (
            computedConfirmLabel
          )}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialog — the canonical entry point. Wraps the panel in a modal.
// ---------------------------------------------------------------------------

export function VaultFilePickerDialog({
  open,
  onOpenChange,
  onFileSelected,
  onFilesSelected,
  multiSelect = false,
  title = 'Select File',
  description,
  confirmLabel,
  mimeFilter,
  allowUpload = true,
  uploadAccept,
  maxUploadSize = DEFAULT_MAX_UPLOAD,
  validateUpload,
}: VaultFilePickerDialogProps) {
  const handleFile = useCallback(
    (file: VaultPickerResult) => {
      onFileSelected?.(file);
      onOpenChange(false);
    },
    [onFileSelected, onOpenChange],
  );
  const handleFiles = useCallback(
    (files: VaultPickerResult[]) => {
      onFilesSelected?.(files);
      onOpenChange(false);
    },
    [onFilesSelected, onOpenChange],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="flex-1 min-h-0">
          <VaultFilePickerPanel
            isActive={open}
            onFileSelected={handleFile}
            onFilesSelected={handleFiles}
            multiSelect={multiSelect}
            confirmLabel={confirmLabel}
            mimeFilter={mimeFilter}
            allowUpload={allowUpload}
            uploadAccept={uploadAccept}
            maxUploadSize={maxUploadSize}
            validateUpload={validateUpload}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
export default VaultFilePickerDialog;
